/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useFacility from '../../hooks/useFacility';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';

// Helper function to extract invoice number from invoice_id
// e.g., "202601270728_INV011" -> 11
const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

// Expandable Row Component for showing invoice details
const AccountRow = ({ account, isSelected, onSelect, isExpanded, onToggleExpand, detailQuery }) => {
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (!isExpanded) return;

    let cancelled = false;
    const fetchInvoiceDetails = async () => {
      setLoadingDetails(true);
      try {
        const response = await axios.get(
          `/api/admin/reports/user-accounts/ledger/${account._id}${detailQuery ? `?${detailQuery}` : ''}`
        );
        if (!cancelled && response.data.success) {
          setInvoiceDetails(response.data.account);
        }
      } catch (error) {
        console.error('Error fetching invoice details:', error);
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    };

    fetchInvoiceDetails();
    return () => { cancelled = true; };
  }, [isExpanded, detailQuery, account._id]);

  const formatCurrency = (amount) => {
    // Normalize negative zero and sub-cent residue so we never render "-$0.00".
    let value = amount || 0;
    if (Math.abs(value) < 0.005) value = 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partially Paid':
        return 'warning';
      case 'Unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <TableRow 
        hover 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          backgroundColor: isSelected ? 'action.selected' : 'inherit'
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelect(account._id, e.target.checked)}
          />
        </TableCell>
        <TableCell>
          <IconButton aria-label="Collapse"
            size="small"
            onClick={() => onToggleExpand(account._id)}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {account.account_number}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{account.user_name}</Typography>
          {account.user_email && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {account.user_email}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip 
            label={account.account_source || 'Unknown'} 
            size="small" 
            variant="outlined"
          />
          {account.in_user_profile === false && (
            <Tooltip title="This account number is no longer listed on the user's profile. Its invoice history is kept here for reference.">
              <Chip
                label="Not on profile"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ ml: 0.5 }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatCurrency(account.total_invoiced)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
            {formatCurrency(account.total_paid)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography 
            variant="body2" 
            color={account.total_outstanding > 0 ? 'error.main' : 'text.secondary'}
            sx={{ fontWeight: 600 }}
          >
            {formatCurrency(account.total_outstanding)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {account.invoice_count}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {formatDate(account.last_invoice_date)}
          </Typography>
          {account.last_invoice_amount && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {formatCurrency(account.last_invoice_amount)}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      
      {/* Expandable Row for Invoice Details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Invoice History
              </Typography>
              
              {loadingDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : invoiceDetails?.invoice_entries?.length > 0 ? (
                <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', verticalAlign: 'top' } }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5', '& .MuiTableCell-root': { verticalAlign: 'middle' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>A/R Account</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Billing Period</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Share %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Paid</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceDetails.invoice_entries.map((entry, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {getInvoiceNumber(entry.invoice_id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.project_title || entry.project_id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {entry.ar_account_number || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(entry.billing_period?.start_date)} - {formatDate(entry.billing_period?.end_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(entry.percent_share || 0).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(entry.amount_share)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(entry.amount_paid)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={entry.remaining_balance > 0 ? 'error.main' : 'text.secondary'}
                          >
                            {formatCurrency(entry.remaining_balance)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={entry.status || 'Unpaid'} 
                            size="small"
                            color={getStatusColor(entry.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No invoice entries found for this account.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const UserAccountsReport = () => {
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get admin's facility preference for preselection
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  // Filter states
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [selectedArAccount, setSelectedArAccount] = useState('all');
  const [includeUnlinked, setIncludeUnlinked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyOutstanding, setShowOnlyOutstanding] = useState(false);
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Selection states
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  
  // Sorting states
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Export state
  const [exporting, setExporting] = useState(false);

  // Preselect facility from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);

  // Accounts Receivable options for the facility filter. "All Facilities" offers
  // every A/R account on file, labelled with the facility it belongs to.
  const arAccountOptions = React.useMemo(() => {
    const relevant = selectedFacility === 'all'
      ? facilities
      : facilities.filter(f => f._id === selectedFacility);

    const options = [];
    const seen = new Set();
    for (const facility of relevant) {
      for (const ar of facility.accounts_receivable || []) {
        const number = (ar?.account_number || '').trim();
        if (!number || seen.has(number)) continue;
        seen.add(number);
        options.push({
          account_number: number,
          label: selectedFacility === 'all'
            ? `${number} — ${facility.facility_name}`
            : `${number}${ar.description ? ` — ${ar.description}` : ''}`
        });
      }
    }
    return options;
  }, [facilities, selectedFacility]);

  // Drop an A/R selection that the newly chosen facility does not offer.
  useEffect(() => {
    if (selectedArAccount === 'all') return;
    if (!arAccountOptions.some(o => o.account_number === selectedArAccount)) {
      setSelectedArAccount('all');
    }
  }, [arAccountOptions, selectedArAccount]);

  // Shared by the row expansion and the CSV export so both see the same slice.
  const detailQuery = React.useMemo(() => {
    const params = new URLSearchParams();
    if (invoiceStartDate) params.append('invoice_start_date', invoiceStartDate);
    if (invoiceEndDate) params.append('invoice_end_date', invoiceEndDate);
    if (selectedArAccount !== 'all') params.append('ar_account', selectedArAccount);
    if (includeUnlinked) params.append('include_unlinked', 'true');
    return params.toString();
  }, [invoiceStartDate, invoiceEndDate, selectedArAccount, includeUnlinked]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedFacility && selectedFacility !== 'all') params.append('facility', selectedFacility);
      if (selectedArAccount !== 'all') params.append('ar_account', selectedArAccount);
      if (includeUnlinked) params.append('include_unlinked', 'true');
      if (searchQuery) params.append('search', searchQuery);
      if (showOnlyOutstanding) params.append('has_outstanding', 'true');
      if (invoiceStartDate) params.append('invoice_start_date', invoiceStartDate);
      if (invoiceEndDate) params.append('invoice_end_date', invoiceEndDate);
      
      const [accountsRes, facilitiesRes] = await Promise.all([
        axios.get(`/api/admin/reports/user-accounts?${params.toString()}`),
        axios.get('/api/facilities')
      ]);
      
      if (accountsRes.data.success) {
        setAccounts(accountsRes.data.accounts);
        setSummary(accountsRes.data.summary);
      }
      
      if (facilitiesRes.data?.length >= 0) {
        setFacilities(Array.isArray(facilitiesRes.data) ? facilitiesRes.data : []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch user accounts report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedFacility, selectedArAccount, includeUnlinked, searchQuery, showOnlyOutstanding, invoiceStartDate, invoiceEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [selectedFacility, selectedArAccount, includeUnlinked, searchQuery, showOnlyOutstanding, invoiceStartDate, invoiceEndDate]);

  const handleSelectAccount = (accountId, isSelected) => {
    const newSelected = new Set(selectedAccounts);
    if (isSelected) {
      newSelected.add(accountId);
    } else {
      newSelected.delete(accountId);
    }
    setSelectedAccounts(newSelected);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedAccounts(new Set(accounts.map(acc => acc._id)));
    } else {
      setSelectedAccounts(new Set());
    }
  };

  const handleToggleExpand = (accountId) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const handleExport = async () => {
    if (selectedAccounts.size === 0) {
      setError('Please select at least one account to export.');
      return;
    }
    
    setExporting(true);
    try {
      const selectedLedgerIds = Array.from(selectedAccounts);
      const detailedData = await Promise.all(
        selectedLedgerIds.map(async (ledgerId) => {
          const response = await axios.get(
            `/api/admin/reports/user-accounts/ledger/${ledgerId}${detailQuery ? `?${detailQuery}` : ''}`
          );
          return response.data.success ? response.data.account : null;
        })
      );
      
      // Build CSV content with invoice details
      const csvRows = [];
      csvRows.push([
        'Account Number',
        'User Name', 
        'Account Source',
        'Total Invoiced',
        'Total Paid',
        'Total Outstanding',
        'Invoice #',
        'A/R Account',
        'Proposal ID',
        'Project Title',
        'Billing Period Start',
        'Billing Period End',
        'Percent Share',
        'Amount Share',
        'Amount Paid',
        'Remaining Balance',
        'Status'
      ].join(','));
      
      for (const account of detailedData) {
        if (!account) continue;
        
        if (account.invoice_entries && account.invoice_entries.length > 0) {
          // Add a row for each invoice entry
          for (const entry of account.invoice_entries) {
            csvRows.push([
              `"${account.account_number}"`,
              `"${account.user_name || ''}"`,
              `"${account.account_source || ''}"`,
              account.total_invoiced || 0,
              account.total_paid || 0,
              account.total_outstanding || 0,
              getInvoiceNumber(entry.invoice_id),
              `"${entry.ar_account_number || ''}"`,
              `"${entry.project_id || ''}"`,
              `"${(entry.project_title || '').replace(/"/g, '""')}"`,
              entry.billing_period?.start_date ? new Date(entry.billing_period.start_date).toLocaleDateString() : '',
              entry.billing_period?.end_date ? new Date(entry.billing_period.end_date).toLocaleDateString() : '',
              entry.percent_share || 0,
              entry.amount_share || 0,
              entry.amount_paid || 0,
              entry.remaining_balance || 0,
              `"${entry.status || ''}"`
            ].join(','));
          }
        } else {
          // Account has no invoices, add summary row
          csvRows.push([
            `"${account.account_number}"`,
            `"${account.user_name || ''}"`,
            `"${account.account_source || ''}"`,
            account.total_invoiced || 0,
            account.total_paid || 0,
            account.total_outstanding || 0,
            '', '', '', '', '', '', '', '', '', '', ''
          ].join(','));
        }
      }
      
      // Create and download CSV
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-accounts-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    // Normalize negative zero and sub-cent residue so we never render "-$0.00".
    let value = amount || 0;
    if (Math.abs(value) < 0.005) value = 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleResetSort = () => {
    setSortColumn(null);
    setSortDirection('asc');
  };

  // Sort accounts
  const sortedAccounts = React.useMemo(() => {
    if (!sortColumn) return accounts;
    
    return [...accounts].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'account_number':
          aValue = (a.account_number || '').toLowerCase();
          bValue = (b.account_number || '').toLowerCase();
          break;
        case 'user_name':
          aValue = (a.user_name || '').toLowerCase();
          bValue = (b.user_name || '').toLowerCase();
          break;
        case 'account_source':
          aValue = (a.account_source || '').toLowerCase();
          bValue = (b.account_source || '').toLowerCase();
          break;
        case 'total_invoiced':
          aValue = a.total_invoiced || 0;
          bValue = b.total_invoiced || 0;
          break;
        case 'total_paid':
          aValue = a.total_paid || 0;
          bValue = b.total_paid || 0;
          break;
        case 'total_outstanding':
          aValue = a.total_outstanding || 0;
          bValue = b.total_outstanding || 0;
          break;
        case 'invoice_count':
          aValue = a.invoice_count || 0;
          bValue = b.invoice_count || 0;
          break;
        case 'last_invoice_date':
          aValue = a.last_invoice_date ? new Date(a.last_invoice_date).getTime() : 0;
          bValue = b.last_invoice_date ? new Date(b.last_invoice_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [accounts, sortColumn, sortDirection]);

  // Paginated accounts
  const paginatedAccounts = sortedAccounts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading && accounts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          User Accounts Report
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Paper sx={{ p: 2, minWidth: 150, bgcolor: '#e3f2fd' }}>
            <Typography variant="caption" color="text.secondary">Total Accounts</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{summary.total_accounts || 0}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 150, bgcolor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Total Invoiced</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{formatCurrency(summary.total_invoiced)}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 150, bgcolor: '#e8f5e9' }}>
            <Typography variant="caption" color="text.secondary">Total Paid</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>{formatCurrency(summary.total_paid)}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 150, bgcolor: '#ffebee' }}>
            <Typography variant="caption" color="text.secondary">Total Outstanding</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>{formatCurrency(summary.total_outstanding)}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 150, bgcolor: '#fff3e0' }}>
            <Typography variant="caption" color="text.secondary">Total Invoices</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{summary.total_invoices || 0}</Typography>
          </Paper>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by account #, name, email, source..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ width: 320 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
          />

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Facility</InputLabel>
            <Select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              label="Facility"
              size="small"
            >
              <MenuItem value="all">All Facilities</MenuItem>
              {facilities.map(facility => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Accounts Receivable</InputLabel>
            <Select
              value={selectedArAccount}
              onChange={(e) => setSelectedArAccount(e.target.value)}
              label="Accounts Receivable"
              size="small"
            >
              <MenuItem value="all">All A/R Accounts</MenuItem>
              {arAccountOptions.map(option => (
                <MenuItem key={option.account_number} value={option.account_number}>
                  {option.label}
                </MenuItem>
              ))}
              {arAccountOptions.length === 0 && (
                <MenuItem value="all" disabled>
                  No A/R accounts set up for this facility
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            label="Invoice From"
            type="date"
            size="small"
            value={invoiceStartDate}
            onChange={(e) => setInvoiceStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            label="Invoice To"
            type="date"
            size="small"
            value={invoiceEndDate}
            onChange={(e) => setInvoiceEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          {(invoiceStartDate || invoiceEndDate) && (
            <Tooltip title="Clear date filter">
              <IconButton onClick={() => { setInvoiceStartDate(''); setInvoiceEndDate(''); }} size="small">
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={showOnlyOutstanding}
                onChange={(e) => setShowOnlyOutstanding(e.target.checked)}
                color="error"
              />
            }
            label="Only Outstanding"
          />

          <Tooltip title="Also show account ledgers whose account number is no longer listed on the user's profile, so no invoice history is hidden.">
            <FormControlLabel
              control={
                <Switch
                  checked={includeUnlinked}
                  onChange={(e) => setIncludeUnlinked(e.target.checked)}
                />
              }
              label="Include removed accounts"
            />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {sortColumn && (
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleResetSort}
              size="small"
            >
              Reset Sort
            </Button>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || selectedAccounts.size === 0}
          >
            {exporting ? 'Exporting...' : `Export Selected (${selectedAccounts.size})`}
          </Button>
        </Box>
      </Box>

      {/* Selection Info */}
      {selectedAccounts.size > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedAccounts.size} account(s) selected
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table sx={{ '& .MuiTableCell-root': { textAlign: 'left', verticalAlign: 'top' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5', '& .MuiTableCell-root': { verticalAlign: 'middle' } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedAccounts.size > 0 && selectedAccounts.size < accounts.length}
                  checked={accounts.length > 0 && selectedAccounts.size === accounts.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: 50 }} />
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('account_number')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Account Number
                  {sortColumn === 'account_number' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('user_name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  User
                  {sortColumn === 'user_name' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('account_source')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Source
                  {sortColumn === 'account_source' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('total_invoiced')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Total Invoiced
                  {sortColumn === 'total_invoiced' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('total_paid')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Total Paid
                  {sortColumn === 'total_paid' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('total_outstanding')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Outstanding
                  {sortColumn === 'total_outstanding' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('invoice_count')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Invoices
                  {sortColumn === 'invoice_count' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('last_invoice_date')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Last Invoice
                  {sortColumn === 'last_invoice_date' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? 'Loading...' : 'No accounts found matching the criteria.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedAccounts.map(account => (
                <AccountRow
                  key={account._id}
                  account={account}
                  isSelected={selectedAccounts.has(account._id)}
                  onSelect={handleSelectAccount}
                  isExpanded={expandedAccounts.has(account._id)}
                  onToggleExpand={handleToggleExpand}
                  detailQuery={detailQuery}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <FormControl size="small">
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            variant="standard"
          >
            <MenuItem value={10}>Show 10 entries</MenuItem>
            <MenuItem value={25}>Show 25 entries</MenuItem>
            <MenuItem value={50}>Show 50 entries</MenuItem>
            <MenuItem value={100}>Show 100 entries</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Showing {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, sortedAccounts.length)} of {sortedAccounts.length}
          </Typography>
          
          <IconButton aria-label="First page"
            onClick={() => handleChangePage(0)}
            disabled={page === 0}
            size="small"
          >
            <FirstPage />
          </IconButton>
          <IconButton aria-label="Previous page"
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            size="small"
          >
            <KeyboardArrowLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>
            Page {page + 1} of {Math.ceil(sortedAccounts.length / rowsPerPage) || 1}
          </Typography>
          <IconButton aria-label="Next page"
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= Math.ceil(sortedAccounts.length / rowsPerPage) - 1}
            size="small"
          >
            <KeyboardArrowRight />
          </IconButton>
          <IconButton aria-label="Last page"
            onClick={() => handleChangePage(Math.ceil(sortedAccounts.length / rowsPerPage) - 1)}
            disabled={page >= Math.ceil(sortedAccounts.length / rowsPerPage) - 1}
            size="small"
          >
            <LastPage />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default UserAccountsReport;
